import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyHeader from '@/components/company/CompanyHeader';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import SearchBar from '@/components/company/filters/SearchBar';
import FilterSelect from '@/components/company/filters/FilterSelect';
import ClearFilterButton from '@/components/company/filters/ClearFilterButton';
import useTenantState from '@/states/tenant.state';
import useMyKpiState, { IMyKpi } from '@/states/myKpi.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const MONTHS_OPTIONS = [
  { label: 'Januari', value: '01' },
  { label: 'Februari', value: '02' },
  { label: 'Maret', value: '03' },
  { label: 'April', value: '04' },
  { label: 'Mei', value: '05' },
  { label: 'Juni', value: '06' },
  { label: 'Juli', value: '07' },
  { label: 'Agustus', value: '08' },
  { label: 'September', value: '09' },
  { label: 'Oktober', value: '10' },
  { label: 'November', value: '11' },
  { label: 'Desember', value: '12' },
];

const STATUS_OPTIONS = [
  { label: 'Draft', value: 'DRAFT' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
];

function StatusBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    DRAFT: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    SUBMITTED: 'bg-blue-100 text-blue-700 border-blue-300',
    APPROVED: 'bg-green-100 text-green-700 border-green-300',
    REJECTED: 'bg-red-100 text-red-700 border-red-300',
  };

  const labelMap: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };

  const cls = map[status?.toUpperCase() ?? ''] ?? 'bg-gray-100 text-gray-600 border-gray-300';
  const label = labelMap[status?.toUpperCase() ?? ''] ?? status ?? '-';

  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{label}</span>;
}

export default function MyKpi() {
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const { currentTenant } = useTenantState();
  const { paginatedData, isLoading, getMyKpis } = useMyKpiState();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [selectedStatus, setSelectedStatus] = useState('');

  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'created_at',
    order: 'desc',
  });

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    let periodParam = undefined;
    if (startMonth || endMonth || selectedYear) {
      const currentYear = new Date().getFullYear().toString();

      const startMatch = MONTHS_OPTIONS.find(m => m.value === startMonth);
      const monthStartLabel = startMatch ? startMatch.label : '%';

      const endMatch = MONTHS_OPTIONS.find(m => m.value === endMonth);
      const monthEndLabel = endMatch ? endMatch.label : '%';

      const yearStr = selectedYear || currentYear;

      periodParam = `${monthStartLabel} - ${monthEndLabel} ${yearStr}`;
    }

    await getMyKpis(currentTenant.id, {
      page: pageConfig.page + 1,
      page_size: pageConfig.limit,
      order: pageConfig.order,
      order_by: pageConfig.orderBy,
      search: debouncedSearch || undefined,
      status: selectedStatus || undefined,
      period: periodParam,
    });
  }, [
    currentTenant?.id,
    pageConfig.page,
    pageConfig.limit,
    pageConfig.order,
    pageConfig.orderBy,
    debouncedSearch,
    selectedStatus,
    startMonth,
    endMonth,
    selectedYear,
    getMyKpis,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayData = paginatedData ?? { content: [], totalElements: 0, totalPages: 0 };

  const columns: MainTableColumn[] = [
    {
      name: 'template_name',
      label: 'Nama Template',
      render: (val: unknown) => String(val ?? '-'),
    },
    {
      name: 'period',
      label: 'Periode',
      render: (val: unknown) => String(val ?? '-'),
    },
    {
      name: 'status',
      label: 'Status',
      render: (val: unknown) => <StatusBadge status={String(val ?? '')} />,
    },
    {
      name: 'progress',
      label: 'Progres',
      render: (val: unknown) => (val !== null && val !== undefined ? `${val}%` : '-'),
    },
    {
      name: 'score',
      label: 'Skor KPI',
      render: (_: unknown, row: IMyKpi) => {
        const isApproved = row.status?.toUpperCase() === 'APPROVED';
        return isApproved && row.score !== null && row.score !== undefined ? String(row.score) : '-';
      },
    },
  ];

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
      const y = (currentYear - 5 + i).toString();
      return { label: y, value: y };
    });
  }, []);

  return (
    <section className="w-full">
      <CompanyHeader title="KPI Saya" description="Monitor status, progres, dan skor KPI Anda." />

      <div className="mx-5 border-b border-gray-200" />

      <div className="flex flex-col md:flex-row md:flex-wrap items-stretch md:items-center justify-start mt-5 px-5 gap-2">
        <SearchBar
          value={searchTerm}
          onChange={val => {
            setSearchTerm(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Cari template..."
          className="w-full md:w-72 min-w-[200px]"
        />
        <FilterSelect
          label="Bulan Mulai"
          options={MONTHS_OPTIONS}
          value={startMonth}
          onChange={val => {
            setStartMonth(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Bulan Selesai"
          options={MONTHS_OPTIONS}
          value={endMonth}
          onChange={val => {
            setEndMonth(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Tahun"
          options={yearOptions}
          value={selectedYear}
          onChange={val => {
            setSelectedYear(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Status"
          options={STATUS_OPTIONS}
          value={selectedStatus}
          onChange={val => {
            setSelectedStatus(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />

        <ClearFilterButton
          onClear={() => {
            setSearchTerm('');
            setStartMonth('');
            setEndMonth('');
            setSelectedYear('');
            setSelectedStatus('');
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
      </div>

      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={{
          content: displayData.content as unknown as { [key: string]: unknown }[],
          totalElements: displayData.totalElements,
          totalPages: displayData.totalPages,
        }}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onView={row => navigate(`/${tenant_id}/kpi-saya/${(row as IMyKpi).id}`)}
        showActions
        sectionClassName="px-5"
      />
    </section>
  );
}
